/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  ProfileOccupationChoices_DemandLevel as DemandLevel,
  ProfileOccupationChoices_FeatureTag as FeatureTag,
} from '__generated__/graphql-types';
import { debounce, omit } from 'lodash';
import moment from 'moment';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { Theme } from '@coursera/cds-core';
import { PageGridContainer, useTheme } from '@coursera/cds-core';
import { track } from '@coursera/event-pulse/sdk';

import { useProfileOccupationChoices } from 'bundles/employability-common/queries/hooks/useProfileOccupationChoices';
import { SurveyQuestionIds, createSurvey, updateSurvey } from 'bundles/enterprise-learner-onboarding/apiUtils';
import { LearnerLevels, LevelSelectionStep } from 'bundles/enterprise-learner-onboarding/components/LevelSelectionStep';
import { NavigationControls } from 'bundles/enterprise-learner-onboarding/components/NavigationControls';
import { ProgressBar } from 'bundles/enterprise-learner-onboarding/components/ProgressBar';
import { RoleInputStep } from 'bundles/enterprise-learner-onboarding/components/RoleInputStep';
import { SelectGoalsStep } from 'bundles/enterprise-learner-onboarding/components/SelectGoalsStep';
import { useProductFeatures } from 'bundles/enterprise-learner-onboarding/features';
import QueryOnboardingResponseByUserId from 'bundles/enterprise-learner-onboarding/queries/QueryOnboardingResponseByUserId.graphql';
import UpdateLearnerOnboardingRemindMeTime from 'bundles/enterprise-learner-onboarding/queries/UpdateOnboardingRemindTime.graphql';
import type {
  QueryOnboardingResponseByUserIdQuery,
  QueryOnboardingResponseByUserIdQueryVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/QueryOnboardingResponseByUserId';
import type {
  LearnerOnboarding_UpdateRemindMeByTimeMutation as UpdateLearnerOnboardingRemindMeByTimeMutation,
  LearnerOnboarding_UpdateRemindMeByTimeMutationVariables as UpdateLearnerOnboardingRemindMeByTimeMutationVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/UpdateOnboardingRemindTime';
import { formatRoleIdToString, handleRedirect } from 'bundles/enterprise-learner-onboarding/utils';
import type { UserEntryAutoCompleteProps } from 'bundles/onboarding-2022/types/AutoCompleteableInputProps';
import type { NonTerminalSurveyData } from 'bundles/onboarding-2022/types/Survey';
import PageHeader from 'bundles/page-header/components/PageHeader';
import { IdTypes } from 'bundles/product-features';
import { TabName } from 'bundles/program-home/components/single-program/SingleProgramTabs';

import _t from 'i18n!nls/program-home';

type PropsFromCaller = {
  programSlug: string;
  programId: string;
  thirdPartyOrganizationId: string;
  programMembership?: { isInvited?: boolean };
  isAuthenticatedUser: boolean;
  isProgramMember: boolean;
  enabledPersonalizedRecs?: boolean;
};

export type Props = PropsFromCaller & UserEntryAutoCompleteProps;

const learnerOnboardingContainer = (theme: Theme) =>
  css({
    padding: '24px 48px',
    maxWidth: '668px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto',
    [theme.breakpoints.down('xs')]: {
      padding: '24px',
    },
  });

const learnerOnboardingGridContainer = css({
  display: 'grid',
  justifyContent: 'center',
});

export type AutoCompleteInput = {
  [k: string]: {
    id?: string;
    otherText?: string;
  };
};

export enum StepNames {
  GOALS = 0,
  FUTURE = 1,
  LEVEL = 2,
}

export enum GoalNames {
  GROW = 'ONBOARDING_GOAL_GROW',
  SWITCH = 'ONBOARDING_GOAL_SWITCH',
  LAND = 'ONBOARDING_GOAL_LAND',
  LEADER = 'ONBOARDING_GOAL_LEADER',
  EXPLORE = 'ONBOARDING_GOAL_EXPLORE',
  CREDENTIALS = 'ONBOARDING_GOAL_CREDENTIALS',
}

export const LearnerOnboardingContainer = ({
  thirdPartyOrganizationId,
  programId,
  programSlug,
  isAuthenticatedUser,
  isProgramMember,
  programMembership,
  enabledPersonalizedRecs,
}: Props) => {
  const router = useRouter();
  const userId = String(user.get()?.id);
  const { occupations } = useProfileOccupationChoices({
    occupationChoiceFilter: {
      occupationTags: [
        { demandLevel: DemandLevel.Low, featureTag: FeatureTag.BaselineV2 },
        { demandLevel: DemandLevel.High, featureTag: FeatureTag.BaselineV2 },
      ],
    },
  });
  const selectGoalsLabel = _t("Select your goals, and we'll recommend courses that will help you achieve them");
  const roleInputLabel = _t('What role would you like to focus on?');
  const levelSelectionLabel = _t('How much experience do you have?');

  const { features: orgFeatures } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganizationId || '',
    skip: !thirdPartyOrganizationId || enabledPersonalizedRecs !== undefined,
    ssr: true,
  });

  const { features: programFeatures } = useProductFeatures({
    idType: IdTypes.Program,
    id: programId || '',
    skip: !programId || enabledPersonalizedRecs !== undefined,
    ssr: true,
  });

  const enablePersonalizedRecommendationsForOrg = orgFeatures.get('enterprise', 'personalizedRecommendations');
  const enablePersonalizedRecommendationsForProgram = programFeatures.get('enterprise', 'personalizedRecommendations');
  const hasEnabledPersonalizedRecommendations =
    enabledPersonalizedRecs || enablePersonalizedRecommendationsForOrg || enablePersonalizedRecommendationsForProgram;

  const showUnsureOption = false;

  const { data: userOnboardingData } = useQuery<
    QueryOnboardingResponseByUserIdQuery,
    QueryOnboardingResponseByUserIdQueryVariables
  >(QueryOnboardingResponseByUserId, {
    variables: { userId },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'network-only',
    skip: !userId,
    ssr: false,
  });

  const hasCompletedOnboarding = Boolean(userOnboardingData?.LearnerOnboarding?.queryResponseByUserId?.isCompleted);
  const prevUserReponse = userOnboardingData?.LearnerOnboarding?.queryResponseByUserId;
  const showLearnerOnboarding =
    isAuthenticatedUser && (isProgramMember || programMembership?.isInvited) && hasEnabledPersonalizedRecommendations;

  const theme = useTheme();
  const onboardingSteps = React.useMemo(() => [_t('Goals'), _t('Future'), _t('Level')], []);
  const [currentStepIdx, setCurrentStepIdx] = React.useState<number>(0);
  const [selectedGoals, setSelectedGoals] = React.useState<GoalNames[]>([]);
  const [disabledGoals, setDisabledGoals] = React.useState<GoalNames[]>([]);
  const [roleInputText, setRoleInputText] = React.useState<string>('');
  const [selectedLevel, setSelectedLevel] = React.useState<LearnerLevels>();
  const [isUnsureRole, setIsUnsureRole] = React.useState<boolean>(false);
  const [surveyData, setSurveyData] = React.useState<NonTerminalSurveyData>({
    responseId: '',
    sectionId: '',
    isTerminal: false,
    title: '',
    questions: [],
  });
  const sharedEventingV3Data = {
    form: { name: 'onboarding_enterprise_multi_page' as const, totalSteps: 3, currentStep: currentStepIdx + 1 },
    enterprise: { programSlug },
  };

  React.useEffect(() => {
    if (hasCompletedOnboarding) {
      const prevSelectedGoals = (prevUserReponse?.goals ?? []).map((goal) => GoalNames[goal]);
      const prevSelectedUnsureOfRole = Boolean(prevUserReponse?.isUnsureOfRole);
      const prevSelectedLevel = prevUserReponse?.level as keyof typeof LearnerLevels;
      const prevSelectedOccupationId = prevUserReponse?.occupationId ?? '';
      setSelectedGoals(prevSelectedGoals);
      setIsUnsureRole(prevSelectedUnsureOfRole);
      setSelectedLevel(LearnerLevels[prevSelectedLevel]);
      setRoleInputText(prevSelectedOccupationId);
    }
  }, [hasCompletedOnboarding, prevUserReponse]);

  const [updateLearnerRemindMeByTime] = useMutation<
    UpdateLearnerOnboardingRemindMeByTimeMutation,
    UpdateLearnerOnboardingRemindMeByTimeMutationVariables
  >(UpdateLearnerOnboardingRemindMeTime, {
    context: { clientName: 'gatewayGql' },
  });

  const handleOnContinueClick = () => {
    setCurrentStepIdx(currentStepIdx + 1);
  };

  const handleOnBackClick = () => {
    setCurrentStepIdx(currentStepIdx - 1);
  };

  const onGoalTileClick = (goal: GoalNames) => {
    if (selectedGoals.includes(goal)) {
      const newGoals = selectedGoals.filter((selectedGoal) => selectedGoal !== goal);
      track('change_select', {
        ...omit(sharedEventingV3Data, 'enterprise'),
        select: {
          name: 'onboarding_learning_goals',
          value: newGoals,
          label: selectGoalsLabel,
          isRequired: true,
        },
      });
      setSelectedGoals(newGoals);
      if (goal === GoalNames.LAND || goal === GoalNames.GROW || goal === GoalNames.SWITCH) {
        setDisabledGoals([]);
      }
    } else {
      track('change_select', {
        ...omit(sharedEventingV3Data, 'enterprise'),
        select: {
          name: 'onboarding_learning_goals',
          value: [...selectedGoals, goal],
          label: selectGoalsLabel,
          isRequired: true,
        },
      });
      setSelectedGoals([...selectedGoals, goal]);
    }
  };

  React.useEffect(() => {
    const getSurvey = async () => {
      const survey = (await createSurvey()) as NonTerminalSurveyData;
      setSurveyData(survey);
    };
    getSurvey();
  }, []);

  React.useEffect(() => {
    if (selectedGoals.includes(GoalNames.LAND)) {
      setDisabledGoals([GoalNames.GROW, GoalNames.SWITCH]);
    } else if (selectedGoals.includes(GoalNames.GROW)) {
      setDisabledGoals([GoalNames.SWITCH, GoalNames.LAND]);
    } else if (selectedGoals.includes(GoalNames.SWITCH)) {
      setDisabledGoals([GoalNames.GROW, GoalNames.LAND]);
    }
  }, [selectedGoals]);

  const redirectToProgramHome = () => {
    if (hasCompletedOnboarding) {
      handleRedirect(`/programs/${programSlug}?currentTab=${TabName.Recommendations}`, router);
      return;
    }
    handleRedirect(`/programs/${programSlug}`, router);
  };

  const onRoleInputTextChange = (e: AutoCompleteInput) => {
    if (e['role-input'].otherText) {
      track('change_select', {
        ...omit(sharedEventingV3Data, 'enterprise'),
        select: {
          name: 'onboarding_desired_occupation',
          value: [e['role-input'].otherText],
          label: roleInputLabel,
          isRequired: true,
        },
      });
      setRoleInputText(e['role-input'].otherText);
    } else if (e['role-input'].id) {
      track('change_select', {
        ...omit(sharedEventingV3Data, 'enterprise'),
        select: {
          name: 'onboarding_desired_occupation',
          value: [e['role-input'].id],
          label: roleInputLabel,
          isRequired: true,
        },
      });
      setRoleInputText(e['role-input'].id);
    }
  };

  const onUnsureRoleChange = () => {
    track('click_checkbox', {
      ...omit(sharedEventingV3Data, 'enterprise'),
      checkbox: { name: 'onboarding_is_unsure_of_role', checked: !!isUnsureRole },
    });
    setIsUnsureRole(!isUnsureRole);
  };

  const onLevelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    track('change_radio', {
      ...omit(sharedEventingV3Data, 'enterprise'),
      radio: {
        id: 'onboarding_current_experience_level',
        value: event.target.value,
        label: levelSelectionLabel,
        isRequired: true,
      },
    });
    setSelectedLevel(event.target.value as LearnerLevels);
  };

  const onSkipLearnerOnboarding = debounce(
    async () => {
      try {
        await updateLearnerRemindMeByTime({
          variables: {
            // TODO: update formatDateTimeDisplay to support this
            // eslint-disable-next-line no-restricted-syntax
            // @ts-expect-error DateTime: string
            remindMeByTime: moment().add(7, 'days'),
          },
        });
        if (hasCompletedOnboarding) {
          track('click_button', { ...sharedEventingV3Data, button: { name: 'onboarding_cancel' } });
        } else {
          track('click_button', { ...sharedEventingV3Data, button: { name: 'onboarding_remind_me_later' } });
        }
        redirectToProgramHome();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    500,
    { leading: true }
  );

  const onCompleteClick = debounce(
    async () => {
      try {
        const { responseId, sectionId, questions } = surveyData;
        await updateSurvey({
          responseId,
          sectionId,
          questions,
          selectedGoals,
          roleInputText: isUnsureRole && showUnsureOption ? '' : roleInputText,
          selectedLevel,
          isUnsureRole: isUnsureRole && showUnsureOption,
        });
        track('click_button', { ...sharedEventingV3Data, button: { name: 'onboarding_submit' } });
        redirectToProgramHome();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    500,
    { leading: true }
  );

  if (!showLearnerOnboarding) {
    redirectToProgramHome();
    return null;
  }

  const goalSelectionCompleted = selectedGoals.length > 0;
  const roleInputCompleted = roleInputText.length > 0 || (isUnsureRole && showUnsureOption);
  const levelSelectionCompleted = selectedLevel !== null;
  const learningGoalSurvey = surveyData?.questions.find((questions) => questions.id === SurveyQuestionIds.goal);
  const learnerLevelSurvey = surveyData?.questions.find((questions) => questions.id === SurveyQuestionIds.level);
  const formattedRoleIdString = formatRoleIdToString(roleInputText);

  return (
    <PageGridContainer css={learnerOnboardingGridContainer}>
      <div data-testid="learner-onboarding-container">
        <div css={learnerOnboardingContainer(theme)}>
          <div>
            <ProgressBar
              progressPercentage={`${Math.ceil(((currentStepIdx + 1) * 100) / onboardingSteps.length)}`}
              currentStep={currentStepIdx + 1}
              totalSteps={onboardingSteps.length}
              currentStepName={onboardingSteps[currentStepIdx]}
            />
            {currentStepIdx === StepNames.GOALS && (
              <SelectGoalsStep
                selectedGoals={selectedGoals}
                disabledGoals={disabledGoals}
                onGoalTileClick={onGoalTileClick}
                isProgramMember={isProgramMember}
                surveyData={learningGoalSurvey}
                label={selectGoalsLabel}
                sharedEventingV3Data={sharedEventingV3Data}
              />
            )}
            {currentStepIdx === StepNames.FUTURE && (
              <RoleInputStep
                roleInputText={formattedRoleIdString}
                allOptions={occupations}
                onRoleInputTextChange={onRoleInputTextChange}
                isUnsureRole={isUnsureRole}
                onUnsureRoleChange={onUnsureRoleChange}
                showUnsureOption={showUnsureOption}
                label={roleInputLabel}
                sharedEventingV3Data={sharedEventingV3Data}
              />
            )}
            {currentStepIdx === StepNames.LEVEL && (
              <LevelSelectionStep
                onLevelChange={onLevelChange}
                surveyData={learnerLevelSurvey}
                selectedLevel={selectedLevel}
                label={levelSelectionLabel}
                sharedEventingV3Data={sharedEventingV3Data}
              />
            )}
            <NavigationControls
              currentStepIdx={currentStepIdx}
              onContinue={handleOnContinueClick}
              onBack={handleOnBackClick}
              canContinue={
                (currentStepIdx === StepNames.GOALS && goalSelectionCompleted) ||
                (currentStepIdx === StepNames.FUTURE && roleInputCompleted) ||
                (currentStepIdx === StepNames.LEVEL && levelSelectionCompleted)
              }
              onSkip={onSkipLearnerOnboarding}
              onCompleteClick={onCompleteClick}
              isFinalStep={
                currentStepIdx === onboardingSteps.length - 1 ||
                (showUnsureOption && isUnsureRole && currentStepIdx === StepNames.FUTURE)
              }
              hasCompletedOnboarding={hasCompletedOnboarding}
            />
          </div>
        </div>
      </div>
    </PageGridContainer>
  );
};

const LearnerOnboardingContainerWithPageHeader: React.FC<Props> = ({
  thirdPartyOrganizationId,
  programId,
  programSlug,
  programMembership,
  isAuthenticatedUser,
  isProgramMember,
  occupations,
}) => {
  return (
    <div>
      <PageHeader
        hideSearch
        hideMetaNav
        thirdPartyOrganizationId={thirdPartyOrganizationId}
        hideEnterprise
        showGDPRBanner
        isEnterprise
        programId={programId}
        logoWrapper="div"
      />
      <LearnerOnboardingContainer
        programId={programId}
        thirdPartyOrganizationId={thirdPartyOrganizationId}
        programSlug={programSlug}
        occupations={occupations}
        isAuthenticatedUser={isAuthenticatedUser}
        isProgramMember={isProgramMember}
        programMembership={programMembership}
      />
    </div>
  );
};

export default compose<Props, PropsFromCaller>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'program_home_v3',
      page: 'enterprise_learner_onboarding',
    },
  }))
)(LearnerOnboardingContainerWithPageHeader);
